import { useState } from "react";
import { Reveal, ChapterHeader } from "./Reveal";
import { useLang } from "../i18n";
import { scrollToId } from "./Navbar";
import { ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

const CAPS = { prod: 935.6, wh: 300, open: 2757 };
const fmt = (n) => n.toLocaleString("sk-SK", { maximumFractionDigits: 1 });

const Slider = ({ label, value, set, max, testid }) => (
  <div>
    <div className="flex items-baseline justify-between mb-2">
      <label htmlFor={testid} className="font-mono text-xs tracking-[0.2em] uppercase text-slate-500">{label}</label>
      <span className="font-mono text-lg font-bold text-safety">{fmt(value)} m²</span>
    </div>
    <input id={testid} data-testid={testid} type="range" min={0} max={max} step={10} value={value}
      onChange={(e) => set(Number(e.target.value))}
      className="w-full h-1.5 bg-steelborder appearance-none cursor-pointer accent-safety" />
  </div>
);

export const Calculator = () => {
  const { t } = useLang();
  const [prod, setProd] = useState(400);
  const [wh, setWh] = useState(100);
  const [open, setOpen] = useState(0);
  const [equip, setEquip] = useState({ crane: true, power: false, air: false, water: false });
  const [term, setTerm] = useState("long");

  const total = prod + wh + open;
  const exceeds = prod > CAPS.prod || wh > CAPS.wh || open > CAPS.open;

  const toggle = (k) => setEquip((e) => ({ ...e, [k]: !e[k] }));

  const send = () => {
    const eq = [
      equip.crane && t("eq_crane"), equip.power && t("eq_power"),
      equip.air && t("eq_air"), equip.water && t("eq_water"),
    ].filter(Boolean).join(", ") || "—";
    const msg = t("calc_msg")
      .replace("{prod}", fmt(prod)).replace("{wh}", fmt(wh)).replace("{open}", fmt(open))
      .replace("{equip}", eq).replace("{term}", t(term === "long" ? "calc_long" : "calc_short"));
    window.dispatchEvent(new CustomEvent("prefill-enquiry", { detail: msg }));
    scrollToId("#kontakt");
  };

  const chips = [
    { k: "crane", label: t("eq_crane"), id: "calc-equip-crane" },
    { k: "power", label: t("eq_power"), id: "calc-equip-power" },
    { k: "air", label: t("eq_air"), id: "calc-equip-air" },
    { k: "water", label: t("eq_water"), id: "calc-equip-water" },
  ];

  return (
    <section id="kalkulacka" data-testid="calc-section" className="py-20 lg:py-28 px-4 sm:px-8 max-w-7xl mx-auto">
      <ChapterHeader number="04" eyebrow={t("calc_eyebrow")} title={t("calc_title")} />

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        <Reveal className="lg:col-span-3 space-y-8">
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed">{t("calc_intro")}</p>

          <Slider label={t("calc_prod")} value={prod} set={setProd} max={1000} testid="calc-prod-slider" />
          <Slider label={t("calc_wh")} value={wh} set={setWh} max={350} testid="calc-warehouse-slider" />
          <Slider label={t("calc_open")} value={open} set={setOpen} max={2800} testid="calc-open-slider" />

          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-slate-500 mb-3">{t("calc_equip")}</p>
            <div className="flex flex-wrap gap-2">
              {chips.map((c) => (
                <button key={c.k} type="button" data-testid={c.id} onClick={() => toggle(c.k)}
                  className={`px-4 py-2.5 border font-mono text-xs tracking-wide transition-colors duration-300 ${equip[c.k] ? "border-safety bg-safety/10 text-safety" : "border-steelborder text-slate-400 hover:border-slate-500"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-slate-500 mb-3">{t("calc_term")}</p>
            <div className="flex gap-2">
              {[["long", t("calc_long")], ["short", t("calc_short")]].map(([v, label]) => (
                <button key={v} type="button" data-testid={`calc-term-${v}`} onClick={() => setTerm(v)}
                  className={`px-5 py-2.5 border font-heading font-bold uppercase tracking-wide text-sm transition-colors duration-300 ${term === v ? "border-safety bg-safety text-ink" : "border-steelborder text-slate-400 hover:border-slate-500"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-2 lg:sticky lg:top-24">
          <div className="border border-steelborder bg-steelcard p-8">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-500 mb-2">{t("calc_total")}</p>
            <p data-testid="calc-total-value" className="font-heading text-6xl font-extrabold text-slate-100 leading-none mb-6">
              {fmt(total)} <span className="text-2xl text-safety">m²</span>
            </p>

            <div data-testid="calc-fit-status"
              className={`flex items-start gap-3 border p-4 mb-8 ${exceeds ? "border-yellow-600/50 bg-yellow-600/5" : "border-safety/40 bg-safety/5"}`}>
              {exceeds
                ? <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                : <CheckCircle2 className="w-5 h-5 text-safety shrink-0 mt-0.5" />}
              <p className="text-sm text-slate-300">{exceeds ? t("calc_exceeds") : t("calc_fits")}</p>
            </div>

            <button onClick={send} data-testid="calc-submit-button"
              className="group w-full flex items-center justify-center gap-3 bg-safety text-ink font-heading font-bold uppercase tracking-wide text-lg px-6 py-4 hover:bg-safety-hover transition-colors duration-300">
              {t("calc_cta")}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
